import {
  PreferencesDialog,
  type PreferencesOutput,
} from '@risksmart-app/atomic-ui';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useObjectToolbarFormStore } from 'src/blocks/object-toolbar/useObjectToolbarFormStore';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

import {
  applyPreferences,
  buildPreferencesSections,
  toStoredPreferences,
} from './applyPreferences';
import { useRiskOverviewPanelDetails } from './hooks/useRiskOverviewPanelDetails';
import { useRiskOverviewPanelPreferences } from './hooks/useRiskOverviewPanelPreferences';
import { useRiskOverviewPanelRatings } from './hooks/useRiskOverviewPanelRatings';
import { RiskOverviewPanelView } from './RiskOverviewPanelView';
import type { RiskOverviewPanelProps } from './types';

const RiskOverviewPanel = ({
  riskId,
  canViewOversight,
  canViewInternalAudit,
}: RiskOverviewPanelProps) => {
  const { t } = useTranslation('common');

  const [preferencesOpen, setPreferencesOpen] = useState(false);

  // Object-level toolbar (CUS-152) — relocate the panel's "preferences" 3-dot
  // into the page-header toolbar's overflow "Edit" group. The toolbar renders
  // above the tabs, outside this panel's subtree, so the opener is bridged up
  // via the shared store (same pattern as FormMenu / the ribbon). Only when a
  // toolbar is actually mounted, else the in-panel 3-dot keeps its place.
  const objectToolbarEnabled = useIsFeatureFlagEnabled('object_toolbar');
  const toolbarMounted = useObjectToolbarFormStore((s) => s.toolbarMounted);
  const setSidebarPrefsConfigState = useObjectToolbarFormStore(
    (s) => s.setSidebarPrefsConfigState
  );
  const clearSidebarPrefsConfigState = useObjectToolbarFormStore(
    (s) => s.clearSidebarPrefsConfigState
  );
  const relocatePreferences = objectToolbarEnabled && toolbarMounted;

  useEffect(() => {
    if (!relocatePreferences) {
      return;
    }
    // `setPreferencesOpen` is a stable state setter, so the published opener is
    // stable — no need to thread it through the effect deps.
    setSidebarPrefsConfigState({
      openPreferences: () => setPreferencesOpen(true),
    });

    return () => clearSidebarPrefsConfigState();
  }, [
    relocatePreferences,
    setSidebarPrefsConfigState,
    clearSidebarPrefsConfigState,
  ]);

  const details = useRiskOverviewPanelDetails(riskId);
  const ratings = useRiskOverviewPanelRatings(riskId, {
    canViewOversight,
    canViewInternalAudit,
  });
  const { preferences, setPreferences } = useRiskOverviewPanelPreferences();

  const orderedRatings = useMemo(
    () => applyPreferences(ratings.data, preferences),
    [ratings.data, preferences]
  );

  const preferencesSections = useMemo(
    () => buildPreferencesSections(details.data, ratings.data, preferences),
    [details.data, ratings.data, preferences]
  );

  const handleSave = (output: PreferencesOutput) => {
    setPreferences(toStoredPreferences(output.sections));
    setPreferencesOpen(false);
  };

  const panelLang = {
    title: t('riskOverviewPanel.title'),
    preferencesAriaLabel: t('riskOverviewPanel.preferencesAriaLabel'),
    ratings: {
      resultDatePrefix: t('riskOverviewPanel.ratings.resultDatePrefix'),
      notRatedBadge: t('riskOverviewPanel.ratings.notRatedBadge'),
      noRatingScore: t('riskOverviewPanel.ratings.noRatingScore'),
      noRatingAvailable: t('riskOverviewPanel.ratings.noRatingAvailable'),
    },
    details: {
      title: t('riskOverviewPanel.details.title'),
      entityLabel: t('riskOverviewPanel.details.entityLabel'),
      statusLabel: t('riskOverviewPanel.details.statusLabel'),
      modifiedByLabel: t('riskOverviewPanel.details.modifiedByLabel'),
      modifiedAtLabel: t('riskOverviewPanel.details.modifiedAtLabel'),
      ownersLabel: t('riskOverviewPanel.details.ownersLabel'),
      emptyValue: t('riskOverviewPanel.details.emptyValue'),
      showMoreLabel: t('riskOverviewPanel.details.showMoreLabel'),
      showFewerLabel: t('riskOverviewPanel.details.showFewerLabel'),
      statusLabels: {
        active: t('riskOverviewPanel.details.statusLabels.active'),
        emerging: t('riskOverviewPanel.details.statusLabels.emerging'),
        monitored: t('riskOverviewPanel.details.statusLabels.monitored'),
        retired: t('riskOverviewPanel.details.statusLabels.retired'),
      },
    },
  };

  const dialogLang = {
    title: t('preferencesDialog.title'),
    description: t('preferencesDialog.description'),
    save: t('preferencesDialog.save'),
    cancel: t('preferencesDialog.cancel'),
  };

  return (
    <>
      <RiskOverviewPanelView
        lang={panelLang}
        state={{
          ratings: { data: orderedRatings, loading: ratings.loading },
          details: { data: details.data, loading: details.loading },
        }}
        actions={{
          // When relocated into the toolbar, drop the in-panel 3-dot (the View
          // hides the button when no handler is supplied) so the action lives in
          // exactly one place.
          onPreferencesClick: relocatePreferences
            ? undefined
            : () => setPreferencesOpen(true),
        }}
      />
      <PreferencesDialog
        lang={dialogLang}
        state={{ open: preferencesOpen, sections: preferencesSections }}
        actions={{ onOpenChange: setPreferencesOpen, onSave: handleSave }}
      />
    </>
  );
};

export { RiskOverviewPanel };
export { RiskOverviewPanelView } from './RiskOverviewPanelView';
